import React, { useRef, useEffect, useState } from "react";
import moment from "jalali-moment";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import useFetch from "../../../../../hooks/useFetch";
import { StatusBadge } from "../../../../shares/dashboard/DashboardWidgets";

/** Exams in this platform are fixed at 10 questions; PreviousExam derives its
 *  "correct" count from the same constant. */
const QUESTION_COUNT = 10;

function Detail({ icon: Icon, label, value }) {
    return (
        <div className="flex items-center gap-xs">
            <Icon sx={{ fontSize: "1.8rem" }} className="shrink-0 text-ink-subtle" />
            <span className="text-sm text-ink-subtle">{label}</span>
            <span className="text-sm font-medium tabular-nums text-ink">{value}</span>
        </div>
    );
}

/**
 * An exam the student can sit right now.
 *
 * All of the start flow is unchanged: clicking calls exam-platform/exam/:id,
 * a 200 clicks a hidden link that opens the exam in a new tab, a 208 means the
 * student is not permitted. Only the presentation changed - the old card was a
 * 10-column grid with the index in a bordered cell and four detail paragraphs
 * separated by border-x rules.
 */
const CurrentExam = ({ exam, index }) => {
    const [started, setStarted] = useState();
    const linkRef = useRef();

    const startExam = useFetch({
        method: "GET",
        url: `exam-platform/exam/${exam.score.exam}`,
        noHeader: false,
        trigger: false,
    });

    useEffect(() => {
        const now = Date.now();
        const timeToCheck = Date.parse(exam?.exam_available_time_start);
        setStarted(now > timeToCheck);
    }, []);

    const handleClick = () => {
        startExam.reFetch();
    };

    useEffect(() => {
        if (startExam.status === 200) {
            linkRef.current.click();
        } else if (startExam.status === 208) {
            toast.error("شما مجاز به شرکت در این آزمون نیستید.", {
                position: "bottom-left",
                rtl: true,
                autoClose: 5000,
                theme: "color",
            });
        }
    }, [startExam.status]);

    return (
        <article
            className="dash-rise flex flex-col gap-lg rounded-panel border border-rule-subtle bg-surface p-xl shadow-card"
            style={{ "--i": index }}
        >
            <div className="flex items-start justify-between gap-md">
                <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-ink">
                        {exam?.exam_name}
                    </h3>
                    {exam?.exam_headline && (
                        <p className="mt-2xs text-base text-ink-muted">
                            {exam.exam_headline}
                        </p>
                    )}
                </div>
                <StatusBadge
                    label={started ? "در دسترس" : "هنوز باز نشده"}
                    tone={started ? "success" : "neutral"}
                />
            </div>

            <div className="flex flex-wrap gap-x-xl gap-y-xs border-t border-rule-subtle pt-lg">
                <Detail
                    icon={EventBusyIcon}
                    label="آخرین مهلت ورود"
                    value={moment(exam?.exam_maxenterance_time, "YYYY/MM/DD HH:mm")
                        .locale("fa")
                        .format("YYYY/MM/DD HH:mm")}
                />
                <Detail
                    icon={TimerOutlinedIcon}
                    label="مدت"
                    value={moment(exam?.exam_duration, "hh:m:s")
                        .locale("fa")
                        .format("mm دقیقه ss ثانیه")}
                />
                <Detail
                    icon={HelpOutlineIcon}
                    label="تعداد سوال"
                    value={QUESTION_COUNT.toLocaleString("fa-IR")}
                />
            </div>

            <div>
                <button
                    type="button"
                    onClick={handleClick}
                    disabled={!started}
                    className="rounded-soft bg-primary px-xl py-sm text-base text-white transition-colors duration-150 hover:bg-primary-hover active:bg-primary-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50 motion-reduce:transition-none"
                >
                    شرکت در آزمون
                </button>
                {!started && (
                    <p className="mt-xs text-sm text-ink-subtle">
                        از{" "}
                        {moment(exam?.exam_available_time_start, "YYYY/MM/DD HH:mm")
                            .locale("fa")
                            .format("YYYY/MM/DD HH:mm")}{" "}
                        می‌توانی شرکت کنی.
                    </p>
                )}
                {/* Clicked programmatically once the server confirms entry. */}
                <Link
                    to={`/dashboard/exam/${exam.score.exam}`}
                    target="_blank"
                    className="hidden"
                    ref={linkRef}
                    aria-hidden="true"
                    tabIndex={-1}
                >
                    شرکت در آزمون
                </Link>
            </div>
        </article>
    );
};

export default CurrentExam;
